import { Badge, Box, Button, Divider, Flex, Grid, HStack, IconButton, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Progress, Spinner, Text, useToast, VStack } from "@chakra-ui/react";
import { ArrowUpRight, CaretLeft, CaretRight, Gauge, Plus, Target, UserGear, UsersFour, Warning } from "@phosphor-icons/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { issbKeys, useGetQuestionnaireRounds, useGetQuestionsOfRound, useInitFoundationRound, useAddYearToFoundationRound } from "../../../hooks/query";
import { constructQueryParamsString, showErrorToast, showInfoToast } from "../../../utils";
import { colors } from "../../../theme/foundations/colors";
import { routes } from "../../../config";
import IssbContainer from "../IssbContainer";
import SecondaryHeader from "../components/SecondaryHeader";
import { H2Dashboard } from "components/text/headings";
import { RoundOverrideProvider } from "../../../context/RoundOverrideContext";
import FoundationSetupController from "./components/FoundationSetupController";

const STAGE_PROGRESS = {
  START: 10,
  AI_QUEUE: 15,
  EXPERT_REVIEW: 25,
  PENDING: 10,
  IN_PROGRESS: 50,
  INTERNAL_REVIEW: 70,
  AUDIT: 85,
  COMPLETE: 100,
};

const getProgressColor = (stageType) => {
  if (stageType === "COMPLETE" || stageType === "AUDIT") return colors.stages.COMPLETE;
  if (stageType === "IN_PROGRESS" || stageType === "INTERNAL_REVIEW") return colors.stages.IN_PROGRESS;
  return "#E53E3E"; // red for early stages
};

const getProgressStyle = (stageType) => {
  if (stageType === "COMPLETE") {
    return {
      background: `repeating-linear-gradient(
        90deg,
        ${colors.stages.COMPLETE} 0px,
        ${colors.stages.COMPLETE} 8px,
        transparent 8px,
        transparent 12px
      )`,
    };
  }
  return {};
};

const QuestionProgress = ({ question, large }) => {
  const progress = STAGE_PROGRESS[question.stageType] ?? 10;
  const color = getProgressColor(question.stageType);
  const dashedStyle = getProgressStyle(question.stageType);

  return (
    <Box minW="140px" maxW={large ? "100%" : "200px"}>
      <Text fontSize="xs" color={large ? "gray.600" : "gray.500"} mb={1}>
        {question.stage}
      </Text>
      {Object.keys(dashedStyle).length > 0 ? (
        <Box h={large ? "14px" : "6px"} borderRadius={large ? "md" : "full"} bg="gray.100" overflow="hidden">
          <Box
            h="100%"
            w={`${progress}%`}
            borderRadius={large ? "md" : "full"}
            sx={dashedStyle}
          />
        </Box>
      ) : (
        <Box h={large ? "14px" : "6px"} borderRadius={large ? "md" : "full"} bg="gray.100" overflow="hidden">
          <Box
            h="100%"
            w={`${progress}%`}
            borderRadius={large ? "md" : "full"}
            bg={color}
          />
        </Box>
      )}
    </Box>
  );
};

const QuestionYearCard = ({ question, prominent, description }) => {
  const navigate = useNavigate();

  return (
    <Box
      borderWidth="1px"
      borderColor={prominent ? "lime" : "gray.200"}
      borderRadius="xl"
      p={6}
      bg={prominent ? "gray.50" : "white"}
      boxShadow="sm"
      cursor="pointer"
      h="100%"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      onClick={() => navigate(`${routes.foundationAnswerDetail}?id=${question.id}`)}
    >
      <Flex justify="space-between" align="flex-start">
        <Box>
          <Text fontSize={prominent ? "lg" : "md"} fontWeight="bold" color="gray.800">
            {question.question}
          </Text>
          {prominent && description && (
            <Text fontSize="sm" color={prominent ? "gray.600" : "gray.500"} mt={1}>
              {description}
            </Text>
          )}
        </Box>
        <Box color={prominent ? "gray.500" : "gray.400"} flexShrink={0} ml={4}>
          <ArrowUpRight size={20} />
        </Box>
      </Flex>
      {prominent && (
        <Flex direction="column" gap={2} mt={4}>
          <HStack spacing={2}>
            <HStack spacing={1} minW="80px">
              <UserGear size={14} color="#718096" />
              <Text fontSize="xs" color="gray.600">Owner</Text>
            </HStack>
            {question.owner ? (
              <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" fontWeight="500" colorScheme="gray">
                {question.owner}
              </Badge>
            ) : (
              <Text fontSize="xs" color="gray.400">Not assigned</Text>
            )}
          </HStack>
          <HStack spacing={2} align="flex-start">
            <HStack spacing={1} minW="80px" mt={0.5}>
              <UsersFour size={14} color="#718096" />
              <Text fontSize="xs" color="gray.600">Assignees</Text>
            </HStack>
            {question.assignees?.length > 0 ? (
              <HStack spacing={2} flexWrap="wrap">
                {question.assignees.map((assignee) => (
                  <Badge key={assignee} px={2} py={1} borderRadius="md" fontSize="body.xxs" fontWeight="500" colorScheme="green">
                    {assignee}
                  </Badge>
                ))}
              </HStack>
            ) : (
              <Text fontSize="xs" color="gray.400">Not assigned</Text>
            )}
          </HStack>
        </Flex>
      )}
      <Box mt={4}>
        <QuestionProgress question={question} large={prominent} />
      </Box>
    </Box>
  );
};

const SectionGrid = ({ questions, description }) => {
  const [page, setPage] = useState(0);

  const sorted = [...questions].sort((a, b) => {
    const yearA = parseInt(a.question.match(/\d{4}/)?.[0]) || 0;
    const yearB = parseInt(b.question.match(/\d{4}/)?.[0]) || 0;
    return yearB - yearA;
  });

  const current = sorted[0];
  const allPrevious = sorted.slice(1);
  const pageItems = allPrevious.slice(page * 2, page * 2 + 2);
  const hasNext = allPrevious.length > (page + 1) * 2;
  const hasPrev = page > 0;

  return (
    <Flex align="center" gap={2}>
      <Grid templateColumns="repeat(2, 1fr)" templateRows="repeat(2, 1fr)" gap={4} flex={1}>
        <Box gridRow="1 / 3" gridColumn="1">
          {current && <QuestionYearCard question={current} prominent description={description} />}
        </Box>
        {pageItems.map((q) => (
          <Box key={q.id} gridColumn="2">
            <QuestionYearCard question={q} />
          </Box>
        ))}
      </Grid>
      {(hasNext || hasPrev) && (
        <Flex direction="column" gap={1} flexShrink={0}>
          {hasPrev && (
            <IconButton
              icon={<CaretLeft size={18} />}
              variant="ghost"
              size="sm"
              color="gray.400"
              _hover={{ color: "darkTeal" }}
              aria-label="Newer years"
              onClick={() => setPage(page - 1)}
            />
          )}
          {hasNext && (
            <IconButton
              icon={<CaretRight size={18} />}
              variant="ghost"
              size="sm"
              color="gray.400"
              _hover={{ color: "darkTeal" }}
              aria-label="Older years"
              onClick={() => setPage(page + 1)}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

const SECTION_ORDER = ["GHG Emissions", "Risks and Opportunities", "Targets"];
const SECTION_ICONS = {
  "GHG Emissions": Gauge,
  "Risks and Opportunities": Warning,
  "Targets": Target,
};
const SECTION_DESCRIPTIONS = {
  "GHG Emissions": "Collate and approve Scope 1, 2 and 3 data for the organisation.",
  "Risks and Opportunities": "Document and confirm all material sustainability risks and opportunities identified for the year.",
  "Targets": "Confirm validity of sustainability targets set.",
};

const FoundationDataPage = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "issb.questionGroup.headings",
  });
  const queryClient = useQueryClient();
  const toast = useToast();

  const paramsString = constructQueryParamsString({ page: 0, size: 500 });
  const { data, isLoading } = useGetQuestionnaireRounds(paramsString);
  const foundationRound = data?.content?.find((r) => r.foundationRound);

  // Auto-init foundation round if it doesn't exist
  const initAttempted = useRef(false);
  const initMutation = useInitFoundationRound({
    onSuccess: () => {
      queryClient.invalidateQueries(issbKeys.questionnaireRound());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  useEffect(() => {
    if (!isLoading && !foundationRound && !initAttempted.current) {
      initAttempted.current = true;
      initMutation.mutate();
    }
  }, [isLoading, foundationRound]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: questions, isLoading: questionsLoading } = useGetQuestionsOfRound(
    null,
    foundationRound?.id,
    { enabled: !!foundationRound?.id }
  );

  const groupedQuestions = useMemo(() => {
    if (!questions) return {};
    const groups = questions.reduce((acc, q) => {
      const key = q.questionGroup || "Ungrouped";
      if (!acc[key]) acc[key] = [];
      acc[key].push(q);
      return acc;
    }, {});
    return groups;
  }, [questions]);

  // Add Year
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const addYearMutation = useAddYearToFoundationRound({
    onSuccess: (data) => {
      queryClient.invalidateQueries(issbKeys.questionList());
      if (data?.length > 0) {
        showInfoToast(toast, `Added ${data.length} questions for ${selectedYear}`);
      } else {
        showInfoToast(toast, `Questions for ${selectedYear} already exist`);
      }
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  if (isLoading || initMutation.isLoading) return <IssbContainer><Spinner /></IssbContainer>;

  if (foundationRound && !(foundationRound.ownerEmails?.length > 0)) {
    return (
      <RoundOverrideProvider round={foundationRound}>
        <FoundationSetupController foundationRound={foundationRound} />
      </RoundOverrideProvider>
    );
  }

  return (
    <SecondaryHeader showRoundSelector={false} showActions={false}>
      <IssbContainer margin="15%">
        <Flex direction="column" gap={2}>
          <Flex justify="space-between" align="flex-start">
            <Box>
              <Text fontSize="heading.3xl" fontWeight="700" color="darkTeal">
                Foundation Data
              </Text>
              <Text maxW="75%" color="gray.500" fontSize="body.m">
                Prepare your sustainability data and insights — complete the key tasks and to gather the
                evidence, metrics, and plans that underpin credible sustainability reporting.
              </Text>
            </Box>
            <HStack spacing={2} flexShrink={0}>
              <NumberInput
                size="sm"
                value={selectedYear}
                onChange={(_, val) => setSelectedYear(val)}
                min={2020}
                max={2050}
                w="100px"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Button
                leftIcon={<Plus size={16} />}
                size="sm"
                variant="outline"
                onClick={() => addYearMutation.mutate(selectedYear)}
                isLoading={addYearMutation.isLoading}
              >
                Add Year
              </Button>
            </HStack>
          </Flex>
        </Flex>

        {questionsLoading && <Spinner />}

        <VStack spacing={8} align="stretch">
          {SECTION_ORDER
            .filter((key) => groupedQuestions[key])
            .map((key) => (
              <Box key={key}>
                <HStack spacing={2} mb={3}>
                  {SECTION_ICONS[key] && React.createElement(SECTION_ICONS[key], { size: 20, color: "#718096" })}
                  <H2Dashboard>{key}</H2Dashboard>
                </HStack>
                <Divider mb={4} borderColor="gray.200" />
                <SectionGrid questions={groupedQuestions[key]} description={SECTION_DESCRIPTIONS[key]} />
              </Box>
            ))}
          {Object.entries(groupedQuestions)
            .filter(([key]) => !SECTION_ORDER.includes(key))
            .map(([groupName, groupQuestions]) => (
              <Box key={groupName}>
                <H2Dashboard mb={3}>
                  {groupName}
                </H2Dashboard>
                <Divider mb={4} borderColor="gray.200" />
                <SectionGrid questions={groupQuestions} />
              </Box>
            ))}
        </VStack>
      </IssbContainer>
    </SecondaryHeader>
  );
};

export default FoundationDataPage;
